import React, { useMemo } from 'react';

import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { formatNumber } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { FC_TRANSFER_DISPLAY_FALLBACK } from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';

const labelClassName = 'text-xs font-semibold uppercase tracking-wide text-muted-foreground';
const valueClassName = 'text-sm font-medium text-foreground';

/**
 * Preview of the lot selected in the picker (tech spec §6.5). When `locationBadges` is omitted, a single badge is built from `selectedRow`.
 *
 * @param {Object} props
 * @param {Record<string, unknown> | null} [props.selectedRow]
 * @param {Array<{ id?: string; label: string; numCases: number | string }>} [props.locationBadges] - Per-location available cases; overrides single-row badge when provided
 * @param {() => void} [props.onAddLot]
 * @param {boolean} [props.addLotDisabled]
 */
export function SelectedTransferLotSummary({ addLotDisabled = false, locationBadges, onAddLot, selectedRow = null }) {
    const badges = useMemo(() => {
        if (Array.isArray(locationBadges) && locationBadges.length) {
            return locationBadges.map((b, i) => ({
                id: b.id ?? `${b.label}-${i}`,
                label: b.label,
                cases: b.numCases,
                looseUnits: b.numLooseUnits,
            }));
        }
        if (selectedRow == null) return [];
        const loc = selectedRow.locationName ?? selectedRow.location_name;
        const cases = selectedRow.numCases;
        const looseUnits = selectedRow.numLooseUnits;
        if (loc === undefined || loc === null || loc === '') return [];
        return [
            {
                id: String(selectedRow.warehouseLocationId ?? selectedRow.warehouse_location_id ?? loc),
                label: String(loc),
                cases,
                looseUnits,
            },
        ];
    }, [locationBadges, selectedRow]);

    if (selectedRow == null) {
        return null;
    }

    const { expiresOn, externalSku, itemName, lotId, numCases, numLooseUnits, unitsPerCase } = selectedRow;
    const expiresRaw = expiresOn;
    const availableCases = numCases;
    const availableLooseUnits = numLooseUnits;
    const itemDisplay = String(itemName ?? FC_TRANSFER_DISPLAY_FALLBACK).trim();

    const unitsDisplay = (() => {
        if (unitsPerCase === undefined || unitsPerCase === null || unitsPerCase === '') {
            return FC_TRANSFER_DISPLAY_FALLBACK;
        }
        return formatNumber(unitsPerCase, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
    })();

    const casesDisplay = (() => {
        if (availableCases === undefined || availableCases === null || availableCases === '') {
            return FC_TRANSFER_DISPLAY_FALLBACK;
        }
        return formatNumber(availableCases, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    })();

    const looseUnitsDisplay = (() => {
        if (availableLooseUnits === undefined || availableLooseUnits === null || availableLooseUnits === '') {
            return FC_TRANSFER_DISPLAY_FALLBACK;
        }
        return formatNumber(availableLooseUnits, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    })();

    return (
        <div className="rounded-md border bg-muted/30 p-4 shadow-sm" data-testid="selected-transfer-lot-summary">
            <h3 className="mb-3 text-left text-sm font-semibold text-foreground">Selected lot</h3>
            <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex min-w-0 flex-col gap-1">
                    <dt className={labelClassName}>Lot ID</dt>
                    <dd className={`${valueClassName} break-words`}>
                        {lotId !== undefined && lotId !== null && lotId !== ''
                            ? String(lotId)
                            : FC_TRANSFER_DISPLAY_FALLBACK}
                    </dd>
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                    <dt className={labelClassName}>External SKU</dt>
                    <dd className={`${valueClassName} break-words`}>
                        {externalSku !== undefined && externalSku !== null && externalSku !== ''
                            ? String(externalSku)
                            : FC_TRANSFER_DISPLAY_FALLBACK}
                    </dd>
                </div>
                <div className="flex min-w-0 flex-col gap-1 sm:col-span-2 lg:col-span-1">
                    <dt className={labelClassName}>Item</dt>
                    <dd className={`${valueClassName} break-words`}>{itemDisplay}</dd>
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                    <dt className={labelClassName}>BBD / Expiration</dt>
                    <dd className={valueClassName}>
                        {formatPurchaseOrderDate(expiresRaw, FC_TRANSFER_DISPLAY_FALLBACK)}
                    </dd>
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                    <dt className={labelClassName}>Units per case</dt>
                    <dd className={valueClassName}>{unitsDisplay}</dd>
                </div>
                <div>
                    <div className="flex min-w-0 flex-col gap-1">
                        <dt className={labelClassName}>Available (this row)</dt>
                        <dd className={valueClassName}>
                            {casesDisplay} cases, {looseUnitsDisplay} loose units
                        </dd>
                    </div>
                </div>
            </dl>

            {badges.length > 0 ? (
                <div className="mt-4 flex flex-col gap-2">
                    <span className={labelClassName}>Locations — available cases</span>
                    <div className="flex flex-wrap items-center gap-2">
                        {badges.map((b) => (
                            <Badge key={b.id} variant="secondary" className="max-w-full font-normal">
                                <span className="truncate">{b.label}</span>
                                <span className="ml-1 shrink-0 tabular-nums opacity-90">
                                    {b.cases ?? 0} cases, {b.looseUnits ?? 0} loose units
                                </span>
                            </Badge>
                        ))}
                        <Button
                            type="button"
                            variant="secondary"
                            className="ml-auto"
                            disabled={addLotDisabled}
                            onClick={() => onAddLot?.()}
                        >
                            + Add Lot
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="mt-2 flex justify-end">
                    <Button type="button" variant="secondary" disabled={addLotDisabled} onClick={() => onAddLot?.()}>
                        + Add Lot
                    </Button>
                </div>
            )}
        </div>
    );
}

SelectedTransferLotSummary.displayName = 'SelectedTransferLotSummary';
